import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { toast } from 'sonner';
import {
	AuthenticationResult,
	AuthenticatorType,
	EventLogger,
	IAuthenticationResponse,
	IOperationResultNoValue,
	IUser,
} from '../../../../../extViewmodels';
import {
	ErrorMessagesViewModelKey,
	IErrorMessageComponentProps,
	IUserSessionComponentProps,
	UserSessionViewModelKey,
} from '../../../../../models/AppState';
import { white } from '../../../../styles/colors';
import { baseStyleSheet, bs } from '../../../../styles/styles';
import { Checkbox } from '../../../Checkbox';
import { Modal } from '../../../Modal';
import { PasswordInput } from '../../../PasswordInput';
import { CheckmarkIcon } from '../../../svgs/icons/CheckmarkIcon';
import { styleSheet } from './styles';

interface IProps extends IErrorMessageComponentProps, IUserSessionComponentProps {
	className?: string;
	expanded?: boolean;
	onCancel?: () => void;
}

enum SecuritySettingsSection {
	Password = 'password',
	TwoFactorAuthentication = 'twoFactorAuthentication',
}

interface IState {
	confirmNewPassword?: string;
	isLoading?: boolean;
	user?: IUser;
	isModalOpen?: boolean;
	isChallenging?: boolean;
	code?: string;
	phoneNumber?: string;
	isAccessConfirmed?: boolean;
	accessConfirmationPassword?: string;
}

class _SecuritySettings extends React.Component<IProps, IState> {
	private mContainerRef = React.createRef<HTMLDivElement>();
	public state: IState = {
		isLoading: false,
		isAccessConfirmed: false,
		user: {
			id: this.props.userSession?.user?.id,
			enabledAuthenticators: [...(this.props.userSession?.user?.enabledAuthenticators || [])],
		},
	};

	public componentDidMount() {
		if (this.props.expanded) {
			this.mContainerRef.current?.scrollIntoView();
		}
	}

	public render() {
		const { userSession } = this.props;
		if (!this.state.user) {
			return null;
		}

		return (
			<>
				<div className={this.state.isLoading && !this.state.isModalOpen ? 'lev-loading-spinner' : 'hide'} />
				<div
					className={this.state.isLoading && !this.state.isModalOpen ? 'hide' : 'settings-formContainer'}
					ref={this.mContainerRef}
				>
					<div className={css(styleSheet.title)}>Password</div>
					<div className={css(styleSheet.editUserPasswordInstructions, baseStyleSheet.mb4)}>
						Make sure your password is at least 8 characters long and includes at least one uppercase letter, one
						lowercase letter, and one number.
					</div>
					<div className={css(baseStyleSheet.mb4)}>
						<span className={css(styleSheet.formFieldLabel)}>Current Password</span>
						<PasswordInput
							inputId='currentPassword'
							inputClassName={css(baseStyleSheet.textFieldStandard)}
							className={css(baseStyleSheet.textFieldStandard)}
							value={this.state.user.password || ''}
							onChange={this.updateFormFieldValue}
						/>
					</div>
					<div className={css(baseStyleSheet.mb4)}>
						<span className={css(styleSheet.formFieldLabel)}>New Password</span>
						<PasswordInput
							inputId='newPassword'
							inputClassName={css(baseStyleSheet.textFieldStandard)}
							className={css(baseStyleSheet.textFieldStandard)}
							value={this.state.user.newPassword || ''}
							onChange={this.updateFormFieldValue}
						/>
					</div>
					<div className={css(baseStyleSheet.mb4)}>
						<span className={css(styleSheet.formFieldLabel)}>Confirm New Password</span>
						<PasswordInput
							inputClassName={css(baseStyleSheet.textFieldStandard)}
							inputId='confirmNewPassword'
							onChange={this.updateFormFieldValue}
							onKeyDown={this.onConfirmPasswordKeyDown}
							value={this.state.confirmNewPassword || ''}
						/>
						<span className={this.getConfirmPasswordMatchClassName()} />
					</div>
					<div className={css(styleSheet.passwordButtonContainer)}>
						<button className={css(baseStyleSheet.ctaButton)} onClick={this.savePasswordClick}>
							Update Password
						</button>
						<button className={css(baseStyleSheet.ctaButtonReverse)} onClick={this.handleCancel}>
							Cancel
						</button>
					</div>
					{userSession?.account?.requireSecondAuthFactor && (
						<>
							<div className={css(styleSheet.title)}>Two-Factor Authentication (2FA)</div>
							<div className={css(baseStyleSheet.mb4)}>
								<span className={css(styleSheet.formFieldLabel, baseStyleSheet.mb4)}>Enabled Authenticators</span>
								<div className={css(styleSheet.body)}>
									<div className={css(styleSheet.checkboxes)}>
										<Checkbox
											id='email-authenticator-enabled'
											checked={this.state.user?.enabledAuthenticators?.includes(AuthenticatorType.Email) || false}
											className={css(styleSheet.checkbox, styleSheet.emailCheckbox)}
											onChange={this.updateCheckboxValue}
										>
											<span>Email</span>
										</Checkbox>

										<div className={css(styleSheet.pushCheckboxContainer)}>
											<Checkbox
												id='sms-authenticator-enabled'
												checked={this.state.user?.enabledAuthenticators?.includes(AuthenticatorType.SMS) || false}
												disabled={userSession?.user?.smsAuthenticator?.phoneNumber ? false : true}
												className={css(styleSheet.checkbox)}
												onChange={this.updateCheckboxValue}
											>
												<span>SMS</span>
											</Checkbox>
										</div>
									</div>
								</div>
								<div className={css(baseStyleSheet.mb2)}>
									<span className={css(styleSheet.formFieldLabel)}>Email</span>
									<div className={css(styleSheet.formValueNonEditable, styleSheet.emailField)}>
										<span>
											{userSession?.user?.emailAuthenticator?.emailAddress
												? userSession.user?.emailAuthenticator?.emailAddress
												: userSession.user?.email}
										</span>
										<a
											className={css(baseStyleSheet.brandLink, styleSheet.linkMargin)}
											href='mailto:support@levitateapp.com'
										>
											Contact support to change
										</a>
									</div>
								</div>
								<div className={css(baseStyleSheet.mb2)}>
									<span className={css(styleSheet.formFieldLabel)}>SMS</span>
									<div className={css(styleSheet.formValueNonEditable, styleSheet.emailField)}>
										<span>
											{userSession?.user?.smsAuthenticator?.phoneNumber
												? userSession.user?.smsAuthenticator?.phoneNumber
												: 'Not configured'}
										</span>
										<button
											className={css(baseStyleSheet.brandLink, styleSheet.linkMargin)}
											onClick={() => this.setState({ isModalOpen: true })}
										>
											Configure authenticator
										</button>
									</div>
								</div>
							</div>
						</>
					)}
				</div>
				{userSession?.account?.requireSecondAuthFactor ? (
					<div className={css(bs.mt5, styleSheet.buttonContainer)}>
						<button className={css(baseStyleSheet.ctaButton)} onClick={this.save2faSettingsClick}>
							Save 2FA Settings
						</button>
						<button className={css(baseStyleSheet.ctaButtonReverse)} onClick={this.handleCancel}>
							Cancel
						</button>
					</div>
				) : null}
				<Modal useDefaultHeader={true} isOpen={this.state.isModalOpen} onRequestClose={() => this.resetModal()}>
					<div className={this.state.isLoading && this.state.isModalOpen ? 'lev-loading-spinner' : 'hide'} />
					<div
						className={this.state.isLoading && this.state.isModalOpen ? 'hide' : 'settings-formContainer'}
						ref={this.mContainerRef}
					>
						{this.state.isAccessConfirmed ? (
							<div>
								<div className={css(styleSheet.title)}>Manage SMS</div>
								<div className={css(styleSheet.smsInstructionsLabel)}>
									<span>Enter a phone number to receive a two-factor authentication code</span>
								</div>

								<div className={css(baseStyleSheet.mb4)}>
									<span className={css(styleSheet.formFieldLabel)}>Phone Number</span>
									<input
										className={css(baseStyleSheet.textFieldStandard)}
										id='2faSMS-modal'
										type='text'
										value={this.state.phoneNumber}
										onChange={this.updateFormFieldValue}
									/>
								</div>
								<div className={css(baseStyleSheet.flex, styleSheet.codeChallengeContainer, baseStyleSheet.mb4)}>
									<button
										className={css(baseStyleSheet.ctaButton)}
										disabled={this.state.phoneNumber === ''}
										onClick={this.configureFactor}
									>
										{this.state.isChallenging ? 'Resend Code' : 'Send Code'}
									</button>
									{this.state.isChallenging && (
										<div className={css(styleSheet.codeSent)}>
											<span>
												<CheckmarkIcon className={css(baseStyleSheet.absoluteCenter)} fillColor={white} />
											</span>
											Code Sent
										</div>
									)}
								</div>

								<div className={css(baseStyleSheet.mb4)}>
									<span className={css(styleSheet.formFieldLabel)}>Authentication Code</span>
									<input
										className={css(baseStyleSheet.textFieldStandard)}
										id='authentication-code'
										type='text'
										value={this.state.code || ''}
										onChange={this.updateFormFieldValue}
									/>
								</div>

								<div className={css(styleSheet.buttonContainer)}>
									<button
										className={css(baseStyleSheet.ctaButton)}
										onClick={this.configureFactor}
										disabled={!this.state.isChallenging || this.state.code === ''}
									>
										Verify Code
									</button>
									<button className={css(baseStyleSheet.ctaButtonReverse)} onClick={() => this.resetModal()}>
										Cancel
									</button>
								</div>
							</div>
						) : (
							<div>
								<div className={css(styleSheet.title)}>Confirm Access</div>
								<div className={css(styleSheet.smsInstructionsLabel)}>
									<span>Please enter your current password to continue</span>
								</div>
								<div className={css(baseStyleSheet.mb4)}>
									<span className={css(styleSheet.formFieldLabel)}>Current Login Password</span>
									<input
										className={css(baseStyleSheet.textFieldStandard)}
										id='acessConfirmationPassword'
										type='password'
										value={this.state.accessConfirmationPassword || ''}
										onChange={this.updateFormFieldValue}
									/>
								</div>
								<div className={css(styleSheet.buttonContainer)}>
									<button
										className={css(baseStyleSheet.ctaButton)}
										disabled={!this.state.accessConfirmationPassword}
										onClick={this.verifyPassword}
									>
										Verify Password
									</button>
									<button className={css(baseStyleSheet.ctaButtonReverse)} onClick={() => this.resetModal()}>
										Cancel
									</button>
								</div>
							</div>
						)}
					</div>
				</Modal>
			</>
		);
	}

	private reset2faSection = () => {
		this.setState({
			...this.state,
			isLoading: false,
			isAccessConfirmed: false,
			accessConfirmationPassword: '',
			user: {
				...this.state.user,
				id: this.props.userSession?.user?.id,
				enabledAuthenticators: [...(this.props.userSession?.user?.enabledAuthenticators || [])],
			},
			isModalOpen: false,
			isChallenging: false,
			code: null,
		});
	};

	private resetPasswordSection = () => {
		this.setState({
			...this.state,
			confirmNewPassword: null,
			isLoading: false,
			user: {
				...this.state.user,
				password: '',
				newPassword: '',
			},
		});
	};

	private handleCancel = () => {
		this.resetPasswordSection();
		this.reset2faSection();
		this.props.onCancel?.();
	};

	private resetModal = () => {
		this.setState({
			isModalOpen: false,
			isAccessConfirmed: false,
			accessConfirmationPassword: '',
			phoneNumber: '',
			code: '',
		});
	};

	private onConfirmPasswordKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.keyCode === 13) {
			this.savePasswordClick();
		}
	};

	private updateFormFieldValue = (e: React.ChangeEvent<HTMLInputElement>) => {
		const elementId = e.target.id;
		const user = this.state.user;

		let confirmNewPassword: string = this.state.confirmNewPassword;

		switch (elementId) {
			case 'currentPassword':
				user.password = e.target.value;
				break;
			case 'newPassword':
				user.newPassword = e.target.value;
				break;
			case 'confirmNewPassword':
				confirmNewPassword = e.target.value;
				break;
			case '2faSMS-modal':
				this.setState({ phoneNumber: e.target.value });
				break;
			case 'authentication-code':
				this.setState({ code: e.target.value });
				break;
			case 'acessConfirmationPassword':
				this.setState({ accessConfirmationPassword: e.target.value });
				break;
			default:
			// Do nothing
		}
		this.setState({ confirmNewPassword, user });
	};

	private showToasterMessage = (message: string) => {
		toast.success(message);
	};

	private displayErrorMessage = (errorMsg: string) => {
		if (this.props.errorMessages) {
			this.props.errorMessages.push({
				messages: [errorMsg],
			});
		}
	};

	private save2faSettingsClick = async () => {
		let dirtyEnabledAuthenticators = false;
		const stateAuth = this.state.user?.enabledAuthenticators || [];
		const propsAuth = this.props.userSession?.user?.enabledAuthenticators || [];
		const areAuthenticatorsDifferent =
			stateAuth.length !== propsAuth.length || stateAuth.some(auth => !propsAuth.includes(auth));

		if (areAuthenticatorsDifferent) {
			dirtyEnabledAuthenticators = true;
		}
		if (dirtyEnabledAuthenticators) {
			const factorsResponse = await this.props.userSession?.webServiceHelper?.callWebServiceAsync(
				'user/factors/enable',
				'PUT',
				this.state.user.enabledAuthenticators
			);

			if (!factorsResponse.success) {
				this.saveError(factorsResponse, 'Two-Factor-Authentication-Changed-Error');
				return;
			}
		}

		this.saveSuccess(SecuritySettingsSection.TwoFactorAuthentication);
	};

	private savePasswordClick = async () => {
		// Validate password fields
		let isValid = true;
		let errorMessage = 'One or more required fields has not been completed';

		let dirtyPasswordFields = false;
		if (this.state.user.password || this.state.user.newPassword || this.state.confirmNewPassword) {
			dirtyPasswordFields = true;
		}

		if (dirtyPasswordFields) {
			if (!this.state.user.password) {
				isValid = false;
				errorMessage = 'Current Password is required';
			} else if (!this.state.user.newPassword) {
				isValid = false;
				errorMessage = 'New Password is required';
			} else if (!this.state.confirmNewPassword) {
				isValid = false;
				errorMessage = 'Confirm New Password is required';
			} else if (this.state.user.newPassword !== this.state.confirmNewPassword) {
				isValid = false;
				errorMessage = 'New Password and Confirm New Password must match';
			} else if (!this.meetsPasswordRequirements('all')) {
				isValid = false;
				errorMessage = 'You did not meet one or more password requirements (see above)';
			}
		}

		if (isValid) {
			if (dirtyPasswordFields) {
				this.setState({ isLoading: true });

				const passwordResponse = await this.props.userSession?.webServiceHelper?.callWebServiceAsync(
					'user/reset-password',
					'POST',
					this.state.user
				);
				if (!passwordResponse.success) {
					this.saveError(passwordResponse, 'Password-Changed-Error');
					return;
				}
			}
		} else {
			this.displayErrorMessage(errorMessage);
			return;
		}

		this.saveSuccess(SecuritySettingsSection.Password);
	};

	private saveSuccess = async (section: SecuritySettingsSection) => {
		let message = '';
		let action = '';
		await this.props.userSession?.load();
		if (section === SecuritySettingsSection.Password) {
			this.resetPasswordSection();
			message = 'Password was changed successfully!';
			action = 'Password-Changed';
		} else if (section === SecuritySettingsSection.TwoFactorAuthentication) {
			this.reset2faSection();
			message = 'Two-Factor Authentication settings were changed successfully!';
			action = 'Two-Factor-Authentication-Changed';
		}

		this.showToasterMessage(message);

		EventLogger.logEvent({
			action,
			category: 'Settings-Personal',
		});
	};

	private saveError = (error: IOperationResultNoValue, action: string) => {
		this.displayErrorMessage(error.systemMessage);
		this.setState({
			...this.state,
			isLoading: false,
		});

		EventLogger.logEvent(
			{
				action,
				category: 'Settings-Personal',
			},
			{ ...error }
		);
	};

	private getConfirmPasswordMatchClassName() {
		const newPassword = this.state.user.newPassword || '';
		const confirmNewPassword = this.state.confirmNewPassword || '';
		const passwordsMatch = !!newPassword && !!confirmNewPassword && newPassword === confirmNewPassword;
		return `confirmPasswordMatch-${passwordsMatch ? 'Pass' : 'Fail'}`;
	}

	private meetsPasswordRequirements(requirement: string) {
		let minEightCharacters = false;
		let minOneUppercase = false;
		let minOneLowercase = false;
		let minOneNumber = false;

		const password = this.state.user.newPassword ? this.state.user.newPassword : '';
		// Check for min length
		if (password.length > 7) {
			minEightCharacters = true;
		}
		// Check for min one uppercase letter
		if (password.length > 0 && password.toLowerCase() !== password) {
			minOneUppercase = true;
		}
		// Check for min one lowercase letter
		if (password.length > 0 && password.toUpperCase() !== password) {
			minOneLowercase = true;
		}
		// Check for min one number
		if (/\d/.test(password)) {
			minOneNumber = true;
		}

		if (requirement === 'all') {
			return minEightCharacters && minOneUppercase && minOneLowercase && minOneNumber;
		} else if (requirement === 'minEightCharacters') {
			return minEightCharacters;
		} else if (requirement === 'minOneUppercase') {
			return minOneUppercase;
		} else if (requirement === 'minOneLowercase') {
			return minOneLowercase;
		} else if (requirement === 'minOneNumber') {
			return minOneNumber;
		}
	}

	private updateCheckboxValue = (e: React.ChangeEvent<HTMLInputElement>) => {
		const elementId = e.target.id;
		const user = this.state.user;
		const enabledAuthenticators = [...(this.state.user.enabledAuthenticators || [])];
		switch (elementId) {
			case 'email-authenticator-enabled':
				if (e.target.checked) {
					enabledAuthenticators.push(AuthenticatorType.Email);
				} else {
					const index = enabledAuthenticators.indexOf(AuthenticatorType.Email);
					if (index > -1) {
						enabledAuthenticators.splice(index, 1);
					}
				}
				break;
			case 'sms-authenticator-enabled':
				if (e.target.checked) {
					enabledAuthenticators.push(AuthenticatorType.SMS);
				} else {
					const index = enabledAuthenticators.indexOf(AuthenticatorType.SMS);
					if (index > -1) {
						enabledAuthenticators.splice(index, 1);
					}
				}
				break;
			default:
			// Do nothing
		}
		this.setState({ user: { ...user, enabledAuthenticators } });
	};

	private configureFactor = async () => {
		this.setState({ isLoading: true });
		const response = await this.props.userSession?.webServiceHelper?.callWebServiceAsync(
			'user/factors/configure',
			'POST',
			{
				twoFactorCode: this.state.code,
				phoneNumber: this.state.phoneNumber,
			}
		);

		if (!response.success) {
			this.saveError(response, 'Two-Factor-Authentication-Changed-Error');
			return;
		}

		const authenticationResponse = response.value as IAuthenticationResponse;
		const result = authenticationResponse.result;
		if (
			result === AuthenticationResult.Failure ||
			result === AuthenticationResult.InvalidCredentials ||
			result === AuthenticationResult.MissingAuthConfig
		) {
			this.displayErrorMessage(authenticationResponse.message);
			this.setState({ isLoading: false });
			return;
		}

		if (result === AuthenticationResult.Challenge) {
			this.setState({ isChallenging: true, isLoading: false });
			return;
		}

		this.saveSuccess(SecuritySettingsSection.TwoFactorAuthentication);
	};

	private verifyPassword = async () => {
		this.setState({ isLoading: true });
		const response = await this.props.userSession?.webServiceHelper?.callWebServiceAsync(
			'user/verify-password',
			'POST',
			this.state.accessConfirmationPassword
		);

		if (!response.success) {
			this.saveError(response, 'Access-Confirmation-Error');
			return;
		}

		this.setState({ isAccessConfirmed: true, accessConfirmationPassword: '', phoneNumber: '', isLoading: false });
	};
}

const SecuritySettingsAsObserver = observer(_SecuritySettings);
export const SecuritySettings = inject(ErrorMessagesViewModelKey, UserSessionViewModelKey)(SecuritySettingsAsObserver);
